import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { CompaniesService } from 'src/app/settings/services/companies.service';
import { CompanyUserService } from 'src/app/settings/services/company-user.service';
import { ContextualMenuService } from 'src/app/settings/services/contextual-menu.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { VoiceSimulationDialogComponent } from 'src/app/shared/components/voice-simulation-dialog/voice-simulation-dialog.component';
import { environment } from 'src/environments/environment';
import constants from 'src/app/shared/constants';

@Component({
  selector: 'app-company-edit-settings',
  templateUrl: './company-edit-settings.component.html',
  styleUrls: ['./company-edit-settings.component.scss'],
})
export class CompanyEditSettingsComponent implements OnInit {
  @ViewChild('settingsForm') settingsForm: NgForm;

  showConfigs = false;
  readonly simulationCompanyId: string | null = environment?.simulationCompanyId ?? null;

  constructor(
    public dialog: MatDialog,
    public authService: AuthService,
    public companiesService: CompaniesService,
    public contextualMenuService: ContextualMenuService,
    private readonly companyUserService: CompanyUserService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.ensureVoiceConfig();
  }

  isKaironEnabled(): boolean {
    return this.authService.isFeatureFlagEnabled(constants.featureFlags.iaKairon, this.companiesService.currentCompany._id);
  }

  isMaiaEnabled(): boolean {
    return this.authService.isFeatureFlagEnabled(constants.featureFlags.iaMaia, this.companiesService.currentCompany._id);
  }

  ensureVoiceConfig(): boolean {
    const company = this.companiesService.currentCompany;
    if (!company?.kairon) return false;
    if (!company.kairon.voice) {
      company.kairon.voice = {
        isEnabled: false,
        callRetryLimit: 3,
        cidFalseAlarm: '',
        cidDuress: '',
        cidNoContact: '',
        cidCommFailure: '',
      };
    }
    return true;
  }

  onKaironIsEnabledChange(company: any, newValue: boolean) {
    if (company.kairon?.isEnabled !== newValue) {
      company.kairon.isEnabled = newValue;
    }
  }

  onRadiusMetersChange(company: any, newValue: number) {
    if (company.kairon?.eventDetectionConfig?.radiusMeters !== newValue) {
      company.kairon.eventDetectionConfig.radiusMeters = newValue;
    }
  }

  onToleranceMinutesChange(company: any, newValue: number) {
    if (company.kairon?.reminderConfig?.toleranceMinutes !== newValue) {
      company.kairon.reminderConfig.toleranceMinutes = newValue;
    }
  }

  onRetryMinutesChange(company: any, newValue: number) {
    if (company.kairon?.reminderConfig?.retryMinutes !== newValue) {
      company.kairon.reminderConfig.retryMinutes = newValue;
    }
  }

  onCidInput(e: Event) {
    const el = e.target as HTMLInputElement;
    let v = (el.value || '').toUpperCase().replace(/[^0-9]/g, '');

    if (v.length > 3) v = v.slice(0, 3);

    el.value = v;
    this.companiesService.currentCompany.kairon.eventDetectionConfig.eventCID = v;
  }

  onLowControlChange(receiver: any, newValue: number) {
    if (receiver.testPHourConfiguration?.lowControl !== newValue) {
      receiver.testPHourConfiguration.lowControl = newValue;
    }
  }

  onGoodControlChange(receiver: any, newValue: number) {
    if (receiver.testPHourConfiguration?.goodControl !== newValue) {
      receiver.testPHourConfiguration.goodControl = newValue;
    }
  }

  testPHourConfigurationLowControlError(testPHourConfiguration: any) {
    return testPHourConfiguration.lowControl >= testPHourConfiguration.goodControl;
  }

  testPHourConfigurationGoodControlError(testPHourConfiguration: any) {
    return testPHourConfiguration.goodControl <= testPHourConfiguration.lowControl;
  }

  onKaironVoiceIsEnabledChange(company: any, newValue: boolean) {
    if (company.kairon?.voice?.isEnabled !== newValue) {
      company.kairon.voice.isEnabled = newValue;
    }
  }

  onCallRetryLimitChange(company: any, newValue: number) {
    if (company.kairon?.voice?.callRetryLimit !== newValue) {
      company.kairon.voice.callRetryLimit = newValue;
    }
  }

  onVoiceCidInput(e: Event, field: 'cidFalseAlarm' | 'cidDuress' | 'cidNoContact' | 'cidCommFailure') {
    const el = e.target as HTMLInputElement;
    let v = (el.value || '').toUpperCase().replace(/[^0-9]/g, '');
    if (v.length > 4) v = v.slice(0, 4);
    el.value = v;
    this.companiesService.currentCompany.kairon.voice[field] = v;
  }

  onMaiaIsEnabledChange(company: any, newValue: boolean) {
    if (company.maia?.isEnabled !== newValue) {
      company.maia.isEnabled = newValue;
    }
  }

  onAlarmEventSendChange(company: any, key: string, newValue: string) {
    if (company.maia?.perimetralConfig?.[key] !== newValue) {
      company.maia.perimetralConfig[key] = newValue;
    }
  }

  openVoiceSimulationDialog(): void {
    this.companyUserService.getPartitionsForSimulation().subscribe({
      next: (res: any) => {
        const dialogRef = this.dialog.open(VoiceSimulationDialogComponent, {
          width: '480px',
          autoFocus: false,
          panelClass: 'voice-sim-dialog',
          data: { partitions: res.partitions || [] },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result?.status !== 'simulate') return;

          this.companyUserService.simulateVoiceCall(result.partitionId, result.phone, result.safeWord).subscribe({
            next: () => {
              this.snackBar.open(
                this.translateService.instant('Settings.VoiceSimulateSuccess'),
                this.translateService.instant('Shared.Close'),
                { duration: 6000 },
              );
            },
            error: (err: any) => {
              this.snackBar.open(
                `${this.translateService.instant('Settings.VoiceSimulateErrorSend')}: ${err?.error?.reason || err.message}`,
                this.translateService.instant('Shared.Close'),
                { duration: 8000 },
              );
            },
          });
        });
      },
      error: () => {
        this.snackBar.open(
          this.translateService.instant('Settings.VoiceSimulateErrorLoad'),
          this.translateService.instant('Shared.Close'),
          { duration: 5000 },
        );
      },
    });
  }
}
